// Sanitizer for markup that is about to be written into an a11y scan sandbox.
//
// The scan iframes are deliberately same-origin with the admin document (axe is
// injected into the frame and the panel reads results back out), so any script
// that runs inside them runs in the authenticated admin session. Admin-authored
// header/footer/hero HTML is therefore stripped of script vectors before it
// reaches innerHTML - innerHTML does not run <script>, but it does fire handler
// attributes such as <img onerror> and <svg onload>.
//
// DOMPurify defaults keep everything axe needs to score the real page: <style>
// blocks, inline `style`, ARIA and data-* attributes, and img/src.

import DOMPurify from "dompurify";

const PURIFY_CONFIG = {
  // iframe: kept so the frame-title rule still reports on embeds; src is blanked
  // below so the sandbox never loads third-party content.
  // link: kept so remote stylesheets still resolve and contrast stays faithful;
  // non-stylesheet rels are dropped below.
  ADD_TAGS: ["iframe", "link"],
  ADD_ATTR: ["title", "allowfullscreen", "frameborder", "loading", "rel"],
  FORBID_ATTR: ["srcdoc"],
  // Without this a leading <style> is hoisted into <head> by the parser and lost,
  // which would make the scan report contrast against unstyled markup.
  FORCE_BODY: true
};

/** Strips script vectors from `html` while preserving the structure and styling axe scores. */
export function sanitizeScanHtml(html: string): string {
  if (!html) return html;

  // RETURN_DOM_FRAGMENT keeps the result in DOMPurify's inert document, so
  // nothing loads or executes while we post-process it.
  const fragment = DOMPurify.sanitize(html, {
    ...PURIFY_CONFIG,
    RETURN_DOM_FRAGMENT: true
  }) as unknown as DocumentFragment;

  fragment.querySelectorAll("iframe").forEach((frame) => {
    frame.removeAttribute("src");
    frame.removeAttribute("srcdoc");
  });
  fragment.querySelectorAll("link").forEach((link) => {
    if (link.getAttribute("rel") !== "stylesheet") link.remove();
  });

  const holder = document.implementation
    .createHTMLDocument("")
    .createElement("div");
  holder.appendChild(fragment);
  return holder.innerHTML;
}
